import React from 'react';
import { useAssetAccess } from 'components/asset-access';
import { LayoutContext } from 'components/layouts/LayoutContext';

interface TooltipPosition {
  x: number;
  y: number;
  top: number;
  left: number;
  caseId: string;
}

export function useContentHover(closeOnEventHandler?: (event?: Event) => void) {
  const [tooltipPosition, setTooltipPosition] =
    React.useState<TooltipPosition | null>(null);
  const { hasCitatorAccess } = useAssetAccess();
  const { raw } = React.useContext(LayoutContext);

  const onHover = React.useCallback(
    (e?: React.MouseEvent<HTMLElement>, caseId?: string) => {
      if (!hasCitatorAccess || raw) return;
      if (e?.target) {
        const eventY = e.pageY;
        const elementTop = [
          ...(e?.target as HTMLElement).getClientRects(),
        ].reduce((top: number | undefined, rect: DOMRect) => {
          if (top === undefined) return rect.y;
          return rect.top <= eventY && rect.bottom >= eventY ? rect.y : top;
        }, undefined);
        const top =
          eventY < (elementTop ?? eventY) ? eventY : elementTop ?? eventY;
        setTooltipPosition({
          x: 0,
          y: 0,
          top: top - 10,
          left: e.pageX,
          caseId: caseId!,
        });
      } else setTooltipPosition(null);
    },
    [hasCitatorAccess, raw],
  );

  return [
    tooltipPosition,
    React.useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        if (!event || !event.target) return onHover();
        switch ((event.target as HTMLElement).tagName) {
          case 'A':
          case 'I':
          case 'B':
          case 'SPAN':
            const caseUrlId =
              (event.target as HTMLElement)?.dataset.caseUrlId ||
              ((event.target as HTMLElement)?.parentNode as HTMLElement)
                ?.dataset.caseUrlId ||
              (
                (event.target as HTMLElement)?.parentNode
                  ?.parentNode as HTMLElement
              )?.dataset.caseUrlId;

            if (caseUrlId) {
              onHover(event, caseUrlId);
            }
            break;
          default:
            onHover();
            break;
        }
      },
      [onHover],
    ),
    React.useCallback(
      (event?: Event) => {
        onHover();
        return closeOnEventHandler && closeOnEventHandler(event);
      },
      [onHover, closeOnEventHandler],
    ),
  ] as const;
}
